<template>
  <UICard
    ref="cardRef"
    :data-connector-id
    class="ui-connector-info"
    :class="mainClasses"
    @click="handleClick"
  >
    <div class="ui-connector-info__header">
      <div v-if="logoSrc" class="ui-connector-info__logo" :style="logoStyles" />
      <div class="ui-connector-info__name">
        <UIInputString
          v-if="editMode"
          v-model="connectorName"
          @save="handleSaveName"
        />
        <span v-else>{{ connectorName }}</span>
      </div>
      <div class="ui-connector-info__status" :class="statusClasses" />
      <UIDottedMenu class="ui-connector-info__menu">
        <template #default="{ hide }">
          <slot name="menu" :data-connector-id :hide :on-edit :is-connectable />
        </template>
      </UIDottedMenu>
    </div>
    <dl class="ui-connector-info__rows">
      <div class="ui-connector-info__row">
        <dt>Access method</dt>
        <dd>{{ connector.access_method }}</dd>
      </div>
      <div class="ui-connector-info__row">
        <dt>Data source</dt>
        <dd>{{ connector.source }}</dd>
      </div>
      <div class="ui-connector-info__row">
        <dt>Status</dt>
        <dd
          v-tooltip="connector.status_message"
          class="ui-connector-info__status-text"
        >
          {{ displayStatus }}
          <UIButtonCopy
            v-if="connector.status_message"
            :text="connector.status_message"
            class="ui-connector-info__copy"
            aria-label="Copy status message"
          />
        </dd>
      </div>
      <div class="ui-connector-info__row">
        <dt>Mapped into repos</dt>
        <dd>{{ displayRepositories }}</dd>
      </div>
      <div v-if="connector.creator_email" class="ui-connector-info__row">
        <dt>Created by</dt>
        <dd class="truncate">{{ connector.creator_email }}</dd>
      </div>
    </dl>
  </UICard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { LinkedDataConnector } from '@types'

import { EMPTY_VALUE_PLACEHOLDER } from '@/const/common'
import { CONNECTOR_SYNC_STATUS_TEXT } from '@/views/LinkedData/utils/const'

import { getConnectorLogo } from './utils/helpers'

import { UICard, UIDottedMenu } from '@ui/core'
import { UIInputString } from '@ui/fields'
import { UIButtonCopy } from '@ui/buttons'

type Props = {
  connector: LinkedDataConnector
  isActive?: boolean
  unclickable?: boolean
}

type Emits = {
  click: [data: string]
  'update:name': [id: string, data: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const editMode = ref(false)

const connectorName = ref(props.connector.name)

const mainClasses = computed(() => ({
  ['ui-connector-info--active']: props.isActive,
  ['ui-connector-info--unclickable']: props.unclickable,
}))

const dataConnectorId = computed(() => `connector-${props.connector.id}`)

const logoSrc = computed(() => getConnectorLogo(props.connector))

const logoStyles = computed(() => ({
  backgroundImage: `url(${logoSrc.value})`,
}))

const displayRepositories = computed(
  () =>
    props.connector.repositories?.map(item => item.name).join(', ') ||
    EMPTY_VALUE_PLACEHOLDER,
)

const displayStatus = computed(
  () => CONNECTOR_SYNC_STATUS_TEXT[props.connector.status],
)

const statusValue = computed(() => displayStatus.value?.toLowerCase())
const statusClasses = computed(() => ({
  [`ui-connector-info__status--${statusValue.value}`]: statusValue.value,
}))

const isConnectable = computed(() => props.connector.is_user_connectable)

const onEdit = () => {
  editMode.value = true
}

const handleClick = () => {
  if (props.unclickable) return
  emit('click', props.connector.id)
}

const handleSaveName = (value: string) => {
  editMode.value = false
  emit('update:name', props.connector.id, value)
}
</script>

<style>
.ui-connector-info {
  @apply relative;
  @apply flex flex-col gap-2;
  @apply outline outline-1 outline-transparent;
  @apply border border-transparent;
  @apply cursor-pointer;

  &:not(&--unclickable) {
    @apply hover:outline-indigo-300 dark:hover:outline-indigo-600;
  }

  &--active {
    @apply !border-indigo-300 dark:!border-indigo-200;
  }

  &__header {
    @apply flex items-center gap-1.5;
  }

  &__name {
    @apply text-base font-medium;
    @apply leading-tight;
    @apply overflow-hidden;
  }

  &__logo {
    @apply size-10;
    @apply shrink-0;
    @apply -my-2;
    @apply border-2 border-gray-100 dark:border-gray-300;
    @apply bg-white bg-center bg-contain bg-no-repeat;
    @apply rounded-full;
  }

  &--active &__logo {
    @apply !border-indigo-300 dark:!border-indigo-200;
  }

  &__menu {
    @apply ml-auto;

    .ui-dotted-menu__area {
      @apply text-body;
    }
  }

  &--active &__name {
    @apply text-indigo-700 dark:text-indigo-300;
  }

  &__rows {
    @apply divide-y divide-gray-200 dark:divide-gray-700;
    @apply text-sm sm:text-xs;
  }

  &__row {
    @apply flex gap-4 items-center;
    @apply w-full py-1;

    dt {
      @apply grow;
      @apply font-medium;
      @apply text-gray-500 dark:text-gray-400;
    }

    &:hover dt {
      @apply text-gray-400 dark:text-gray-300;
    }

    dd {
      @apply mt-0;
      @apply text-gray-900 dark:text-gray-300;
      @apply sm:text-right;
    }

    &:hover dd {
      @apply text-gray-500 dark:text-gray-200;
    }
  }

  &__status {
    @apply size-1.5 rounded-full;
    @apply shrink-0;

    &--success {
      @apply bg-green-500 dark:bg-green-400;
    }

    &--warning {
      @apply bg-yellow-500 dark:bg-yellow-400;
    }

    &--error {
      @apply bg-red-500 dark:bg-red-400;
    }
  }

  &__status-text {
    @apply flex gap-2 items-center;
  }
  &__copy {
    .ui-button {
      @apply w-auto h-auto;
      @apply bg-transparent hover:bg-transparent;

      &__icon {
        @apply w-4 h-4;
        @apply text-gray-900 dark:text-gray-300;
      }
    }
  }
}
</style>
